import React from 'react';

import {
  type CrawlerFilter,
  NO_CRAWLERS,
  type QueryableCrawlerFilter,
  collapseToAll,
  toPicker,
} from './CrawlerFilter';
import { type CrawlerCategoryScheme, pickerCategories } from './Crawlers';
import { useNoCrawlersSelected } from './NoCrawlersSelectedHook';

export type CrawlerFilterUpdate = { crawlerFilter?: CrawlerFilter };

type CrawlerSelectionInput = {
  urlFilter: QueryableCrawlerFilter;
  everCrawled: { data?: string[]; isSuccess: boolean };
  categoryScheme: CrawlerCategoryScheme;
  isPrinting: boolean;
};

export type CrawlerSelection = {
  crawlerUniverse: string[];
  effectiveCrawlerFilter: QueryableCrawlerFilter;
  pageCrawlerFilter: CrawlerFilter;
  noCrawlersInScope: boolean;
  noteFiltersUpdate: (update: CrawlerFilterUpdate) => void;
};

export function useCrawlerSelection({
  urlFilter,
  everCrawled,
  categoryScheme,
  isPrinting,
}: CrawlerSelectionInput): CrawlerSelection {
  const { noCrawlersSelected, setNoCrawlersSelected, noteOwnNavigation } = useNoCrawlersSelected();

  const noteFiltersUpdate = React.useCallback(
    ({ crawlerFilter }: CrawlerFilterUpdate) => {
      if (crawlerFilter) setNoCrawlersSelected(crawlerFilter.kind === 'none');
      noteOwnNavigation();
    },
    [setNoCrawlersSelected, noteOwnNavigation]
  );

  const crawlerUniverse = React.useMemo(
    () => pickerCategories(everCrawled.data ?? [], categoryScheme),
    [everCrawled.data, categoryScheme]
  );
  const effectiveCrawlerFilter = collapseToAll(urlFilter, crawlerUniverse, categoryScheme);
  const pageCrawlerFilter: CrawlerFilter = noCrawlersSelected ? NO_CRAWLERS : effectiveCrawlerFilter;
  const linkNamesNoKnownCrawler =
    everCrawled.isSuccess && toPicker(effectiveCrawlerFilter, crawlerUniverse, categoryScheme) === null;
  const noCrawlersInScope = !isPrinting && (noCrawlersSelected || linkNamesNoKnownCrawler);

  return { crawlerUniverse, effectiveCrawlerFilter, pageCrawlerFilter, noCrawlersInScope, noteFiltersUpdate };
}
